import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';

import {
  SectionTitle,
  Heading,
  BlueText,
  Button,
} from '../styles/Global.styled.js';

import {
  FooterSection,
  FooterWrapper,
  ContactCard,
  ContactForm,
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  StatusMessage,
  FooterBottom,
  FooterText,
} from '../styles/Footer.styled.js';

// ─── EmailJS credentials ────────────────────────────
// 1. Sign up at https://www.emailjs.com (free)
// 2. Create an Email Service (Gmail, Outlook, etc.)
// 3. Create an Email Template with variables: {{from_name}}, {{from_email}}, {{message}}
// 4. Replace the values below with your own:
const EMAILJS_SERVICE_ID = 'service_74b306u';
const EMAILJS_TEMPLATE_ID = 'template_xtjl97b';
const EMAILJS_PUBLIC_KEY = 'HkC0rqrdsJP8ONEcT';
// ─────────────────────────────────────────────────────

function Footer() {
  const formRef = useRef();
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    const name = formRef.current.from_name.value.trim();
    const email = formRef.current.from_email.value.trim();
    const message = formRef.current.message.value.trim();

    // Basic validation
    if (!name || !email || !message) {
      setStatus({ type: 'error', message: 'Please fill in all fields.' });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    setSending(true);

    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY
      );
      setStatus({
        type: 'success',
        message: 'Message sent successfully! I\'ll get back to you soon.',
      });
      formRef.current.reset();
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again or email me directly.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <FooterSection id="Contact">
      <FooterWrapper>
        <SectionTitle align="center">Get In Touch</SectionTitle>
        <Heading as="h2" size="h2" align="center">
          Contact <BlueText>Me</BlueText>
        </Heading>

        <ContactCard>
          <ContactForm ref={formRef} onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel htmlFor="name">Name</FormLabel>
              <FormInput
                id="name"
                name="from_name"
                type="text"
                placeholder="Your full name"
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="email">Email</FormLabel>
              <FormInput
                id="email"
                name="from_email"
                type="email"
                placeholder="your@email.com"
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="message">Message</FormLabel>
              <FormTextarea
                id="message"
                name="message"
                placeholder="Tell me about your project..."
                required
              />
            </FormGroup>

            {status && (
              <StatusMessage variant={status.type}>
                {status.message}
              </StatusMessage>
            )}

            <Button
              as="button"
              type="submit"
              disabled={sending}
              style={{
                width: '100%',
                justifyContent: 'center',
                opacity: sending ? 0.7 : 1,
                cursor: sending ? 'not-allowed' : 'pointer',
              }}
            >
              {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </ContactForm>
        </ContactCard>

        <FooterBottom>
          <FooterText>
            Designed & Built by{' '}
            <a
              href="https://www.linkedin.com/in/muhammed-arshad-628b23193"
              target="_blank"
              rel="noopener noreferrer"
            >
              Muhammed Arshad
            </a>{' '}
            &copy; {new Date().getFullYear()}
          </FooterText>
        </FooterBottom>
      </FooterWrapper>
    </FooterSection>
  );
}

export default Footer;
